// src/app/TemplateManager.js - Admin Template Management for TrackerSheets Marketplace
import React, { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Edit,
  Eye,
  Copy,
  Archive,
  Trash2,
  X,
  Star,
  Download,
  DollarSign,
  FileText,
  BarChart3,
  MoreVertical,
  Save,
  ArrowLeft,
} from 'lucide-react';

// ---------- Constants ----------

const PLATFORMS = [
  'Google Sheets',
  'Excel',
  'Notion',
  'Airtable',
  'Monday',
  'Trello',
  'Other',
];

const CATEGORIES = [
  'Project Management',
  'Finance & Accounting',
  'Marketing',
  'Human Resources',
  'Sales & CRM',
  'Operations',
  'Education',
  'Health & Fitness',
  'Personal Productivity',
  'Data Analysis',
];

const DIFFICULTY_OPTIONS = ['Beginner', 'Intermediate', 'Advanced'];

// ---------- Mock Data ----------

const MOCK_TEMPLATES = [
  {
    id: '1',
    title: 'Project Tracker Pro',
    slug: 'project-tracker-pro',
    platform: 'Google Sheets',
    category: 'Project Management',
    status: 'published',
    price: 29.99,
    pricing: 'premium',
    downloads: 1243,
    rating: 4.8,
    thumbnail: '/images/templates/project-tracker.png',
    shortDescription: 'Complete project tracking with Gantt charts and dashboards.',
    longDescription: '# Project Tracker Pro\n\nA comprehensive project management template...',
    templateUrl: 'https://docs.google.com/spreadsheets/d/abc123',
    previewUrl: 'https://preview.example.com/project-tracker',
    videoUrl: '',
    screenshots: ['/images/screenshots/pt-1.png', '/images/screenshots/pt-2.png'],
    tags: ['project management', 'gantt', 'dashboard'],
    features: ['Automated Gantt charts', 'Team workload view', 'Budget tracking'],
    useCases: ['Software development', 'Construction projects', 'Event planning'],
    difficulty: 'Intermediate',
    featured: true,
    createdAt: '2025-11-15',
  },
  {
    id: '2',
    title: 'Monthly Budget Planner',
    slug: 'monthly-budget-planner',
    platform: 'Excel',
    category: 'Finance & Accounting',
    status: 'published',
    price: 0,
    pricing: 'free',
    downloads: 3421,
    rating: 4.5,
    thumbnail: '/images/templates/budget-planner.png',
    shortDescription: 'Track income, expenses, and savings goals month by month.',
    longDescription: '# Monthly Budget Planner\n\nSimple yet powerful budget tracking...',
    templateUrl: 'https://example.com/budget-template.xlsx',
    previewUrl: '',
    videoUrl: 'https://youtube.com/watch?v=xyz',
    screenshots: [],
    tags: ['budget', 'finance', 'personal'],
    features: ['Auto-categorization', 'Savings goals', 'Visual charts'],
    useCases: ['Personal budgeting', 'Household finances'],
    difficulty: 'Beginner',
    featured: false,
    createdAt: '2025-12-01',
  },
  {
    id: '3',
    title: 'Marketing Campaign Dashboard',
    slug: 'marketing-campaign-dashboard',
    platform: 'Notion',
    category: 'Marketing',
    status: 'draft',
    price: 19.99,
    pricing: 'premium',
    downloads: 0,
    rating: 0,
    thumbnail: '/images/templates/marketing-dash.png',
    shortDescription: 'Plan, execute, and measure marketing campaigns in one place.',
    longDescription: '# Marketing Campaign Dashboard\n\nAll-in-one marketing hub...',
    templateUrl: '',
    previewUrl: '',
    videoUrl: '',
    screenshots: ['/images/screenshots/mc-1.png'],
    tags: ['marketing', 'campaigns', 'analytics'],
    features: ['Campaign calendar', 'ROI calculator', 'Content planner'],
    useCases: ['Digital marketing teams', 'Freelance marketers'],
    difficulty: 'Intermediate',
    featured: false,
    createdAt: '2026-01-10',
  },
  {
    id: '4',
    title: 'Employee Onboarding Tracker',
    slug: 'employee-onboarding-tracker',
    platform: 'Airtable',
    category: 'Human Resources',
    status: 'archived',
    price: 14.99,
    pricing: 'premium',
    downloads: 587,
    rating: 4.2,
    thumbnail: '/images/templates/onboarding.png',
    shortDescription: 'Streamline your employee onboarding process with checklists.',
    longDescription: '# Employee Onboarding Tracker\n\nNever miss an onboarding step...',
    templateUrl: 'https://airtable.com/shrXYZ',
    previewUrl: 'https://preview.example.com/onboarding',
    videoUrl: '',
    screenshots: [],
    tags: ['HR', 'onboarding', 'checklist'],
    features: ['Task checklists', 'Progress tracking', 'Email notifications'],
    useCases: ['HR departments', 'Small businesses', 'Startups'],
    difficulty: 'Beginner',
    featured: false,
    createdAt: '2025-09-20',
  },
  {
    id: '5',
    title: 'Sales Pipeline CRM',
    slug: 'sales-pipeline-crm',
    platform: 'Google Sheets',
    category: 'Sales & CRM',
    status: 'published',
    price: 39.99,
    pricing: 'premium',
    downloads: 892,
    rating: 4.7,
    thumbnail: '/images/templates/sales-crm.png',
    shortDescription: 'Manage leads, deals, and revenue forecasting in a spreadsheet.',
    longDescription: '# Sales Pipeline CRM\n\nA lightweight CRM built entirely in Google Sheets...',
    templateUrl: 'https://docs.google.com/spreadsheets/d/def456',
    previewUrl: 'https://preview.example.com/sales-crm',
    videoUrl: 'https://youtube.com/watch?v=abc',
    screenshots: ['/images/screenshots/crm-1.png', '/images/screenshots/crm-2.png', '/images/screenshots/crm-3.png'],
    tags: ['sales', 'CRM', 'pipeline', 'forecasting'],
    features: ['Kanban pipeline view', 'Revenue forecasting', 'Contact management', 'Activity logging'],
    useCases: ['Sales teams', 'Solopreneurs', 'Consultants'],
    difficulty: 'Advanced',
    featured: true,
    createdAt: '2025-10-05',
  },
];

// ---------- Helper ----------

const generateSlug = (title) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const emptyForm = () => ({
  id: null,
  title: '',
  slug: '',
  platform: 'Google Sheets',
  category: CATEGORIES[0],
  status: 'draft',
  pricing: 'free',
  price: 0,
  shortDescription: '',
  longDescription: '',
  templateUrl: '',
  previewUrl: '',
  thumbnail: '',
  screenshots: [],
  videoUrl: '',
  tags: '',
  features: [''],
  useCases: [''],
  difficulty: 'Beginner',
  featured: false,
});

// ---------- Sub-components ----------

const StatusBadge = ({ status }) => {
  const styles = {
    published: 'bg-green-100 text-green-800',
    draft: 'bg-yellow-100 text-yellow-800',
    archived: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status] || styles.draft}`}>
      {status}
    </span>
  );
};

const PlatformBadge = ({ platform }) => {
  const colors = {
    'Google Sheets': 'bg-green-50 text-green-700 border-green-200',
    Excel: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Notion: 'bg-gray-50 text-gray-700 border-gray-200',
    Airtable: 'bg-blue-50 text-blue-700 border-blue-200',
    Monday: 'bg-purple-50 text-purple-700 border-purple-200',
    Trello: 'bg-sky-50 text-sky-700 border-sky-200',
    Other: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded border text-xs font-medium ${colors[platform] || colors.Other}`}>
      {platform}
    </span>
  );
};

// ---------- Main Component ----------

const TemplateManager = ({ setCurrentPage }) => {
  // List state
  const [templates, setTemplates] = useState(MOCK_TEMPLATES);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPricing, setFilterPricing] = useState('All');
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [isEditing, setIsEditing] = useState(false);

  // Close action dropdown on outside click
  useEffect(() => {
    const handler = () => setActiveDropdown(null);
    if (activeDropdown !== null) {
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [activeDropdown]);

  // ---------- Filtering ----------

  const filtered = templates.filter((t) => {
    if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterPlatform !== 'All' && t.platform !== filterPlatform) return false;
    if (filterStatus !== 'All' && t.status !== filterStatus.toLowerCase()) return false;
    if (filterPricing !== 'All' && t.pricing !== filterPricing.toLowerCase()) return false;
    return true;
  });

  // ---------- Stats ----------

  const stats = {
    total: templates.length,
    published: templates.filter((t) => t.status === 'published').length,
    drafts: templates.filter((t) => t.status === 'draft').length,
    downloads: templates.reduce((s, t) => s + t.downloads, 0),
    revenue: templates
      .filter((t) => t.pricing === 'premium')
      .reduce((s, t) => s + t.downloads * t.price * 0.15, 0), // mock ~15% conversion
  };

  // ---------- Actions ----------

  const openCreate = () => {
    setForm(emptyForm());
    setIsEditing(false);
    setShowModal(true);
  };

  const openEdit = (template) => {
    setForm({
      ...template,
      tags: Array.isArray(template.tags) ? template.tags.join(', ') : template.tags,
      features: template.features.length ? [...template.features] : [''],
      useCases: template.useCases.length ? [...template.useCases] : [''],
      thumbnail: template.thumbnail || '',
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDuplicate = (template) => {
    const dup = {
      ...template,
      id: String(Date.now()),
      title: `${template.title} (Copy)`,
      slug: `${template.slug}-copy`,
      status: 'draft',
      downloads: 0,
      rating: 0,
      createdAt: new Date().toISOString().slice(0, 10),
    };
    setTemplates((prev) => [dup, ...prev]);
    // TODO: Insert duplicated template into Supabase
  };

  const handleArchive = (id) => {
    setTemplates((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status: 'archived' } : t))
    );
    // TODO: Update template status to 'archived' in Supabase
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this template? This cannot be undone.')) {
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      // TODO: Delete template from Supabase
    }
  };

  const handleSave = () => {
    const parsed = {
      ...form,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      features: form.features.filter((f) => f.trim() !== ''),
      useCases: form.useCases.filter((u) => u.trim() !== ''),
      price: form.pricing === 'free' ? 0 : parseFloat(form.price) || 0,
      screenshots: form.screenshots.filter((s) => s.trim() !== ''),
    };

    if (isEditing) {
      setTemplates((prev) =>
        prev.map((t) => (t.id === parsed.id ? { ...t, ...parsed } : t))
      );
      // TODO: Update template in Supabase via supabase.from('templates').update(...)
    } else {
      const newTemplate = {
        ...parsed,
        id: String(Date.now()),
        downloads: 0,
        rating: 0,
        createdAt: new Date().toISOString().slice(0, 10),
      };
      setTemplates((prev) => [newTemplate, ...prev]);
      // TODO: Insert new template into Supabase via supabase.from('templates').insert(...)
    }

    setShowModal(false);
  };

  // ---------- Form helpers ----------

  const updateForm = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'title') {
        next.slug = generateSlug(value);
      }
      if (field === 'pricing' && value === 'free') {
        next.price = 0;
      }
      return next;
    });
  };

  const updateListItem = (field, index, value) => {
    setForm((prev) => {
      const list = [...prev[field]];
      list[index] = value;
      return { ...prev, [field]: list };
    });
  };

  const addListItem = (field) => {
    setForm((prev) => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeListItem = (field, index) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  // Screenshot helpers
  const addScreenshot = () => {
    setForm((prev) => ({ ...prev, screenshots: [...prev.screenshots, ''] }));
  };

  const updateScreenshot = (index, value) => {
    setForm((prev) => {
      const list = [...prev.screenshots];
      list[index] = value;
      return { ...prev, screenshots: list };
    });
  };

  const removeScreenshot = (index) => {
    setForm((prev) => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index),
    }));
  };

  // ---------- Render ----------

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCurrentPage && setCurrentPage('dashboard')}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Template Manager</h1>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <Plus size={16} />
          Create New Template
        </button>
      </div>

      {/* Stats Bar */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Templates', value: stats.total, icon: FileText, color: 'text-blue-600' },
            { label: 'Published', value: stats.published, icon: BarChart3, color: 'text-green-600' },
            { label: 'Drafts', value: stats.drafts, icon: Edit, color: 'text-yellow-600' },
            { label: 'Total Downloads', value: stats.downloads.toLocaleString(), icon: Download, color: 'text-purple-600' },
            { label: 'Total Revenue', value: `$${stats.revenue.toFixed(2)}`, icon: DollarSign, color: 'text-emerald-600' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={color} />
                <span className="text-xs text-gray-500 font-medium">{label}</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 pb-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Platforms</option>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Status</option>
            <option value="Published">Published</option>
            <option value="Draft">Draft</option>
            <option value="Archived">Archived</option>
          </select>
          <select
            value={filterPricing}
            onChange={(e) => setFilterPricing(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Pricing</option>
            <option value="Free">Free</option>
            <option value="Premium">Premium</option>
          </select>
        </div>
      </div>

      {/* Templates Table */}
      <div className="px-6 pb-6">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Template</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Platform</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Price</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Downloads</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Rating</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-500">
                      No templates match the current filters.
                    </td>
                  </tr>
                )}
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    {/* Thumbnail + Title */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                          {t.thumbnail ? (
                            <img src={t.thumbnail} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <FileText size={16} className="text-gray-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 truncate max-w-[200px]">{t.title}</p>
                          {t.featured && (
                            <span className="text-xs text-amber-600 flex items-center gap-0.5">
                              <Star size={10} fill="currentColor" /> Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><PlatformBadge platform={t.platform} /></td>
                    <td className="px-4 py-3 text-gray-700">{t.category}</td>
                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-3 text-gray-700">
                      {t.pricing === 'free' ? (
                        <span className="text-green-600 font-medium">Free</span>
                      ) : (
                        `$${t.price.toFixed(2)}`
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{t.downloads.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      {t.rating > 0 ? (
                        <span className="flex items-center gap-1 text-gray-700">
                          <Star size={12} className="text-amber-400" fill="currentColor" />
                          {t.rating}
                        </span>
                      ) : (
                        <span className="text-gray-400">--</span>
                      )}
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(t)}
                          title="Edit"
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600"
                        >
                          <Edit size={15} />
                        </button>
                        <button
                          onClick={() => {
                            // TODO: Open preview in new tab using previewUrl
                            if (t.previewUrl) window.open(t.previewUrl, '_blank');
                          }}
                          title="Preview"
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          onClick={() => handleDuplicate(t)}
                          title="Duplicate"
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-purple-600"
                        >
                          <Copy size={15} />
                        </button>
                        {/* More actions dropdown */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(activeDropdown === t.id ? null : t.id);
                            }}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                          >
                            <MoreVertical size={15} />
                          </button>
                          {activeDropdown === t.id && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-40 py-1">
                              <button
                                onClick={() => { handleArchive(t.id); setActiveDropdown(null); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                              >
                                <Archive size={14} /> Archive
                              </button>
                              <button
                                onClick={() => { handleDelete(t.id); setActiveDropdown(null); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                              >
                                <Trash2 size={14} /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ===== Create / Edit Modal (full-screen overlay) ===== */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowModal(false)}
          />
          {/* Side panel */}
          <div className="relative ml-auto w-full max-w-2xl bg-white h-full overflow-y-auto shadow-xl">
            {/* Panel header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-gray-900">
                {isEditing ? 'Edit Template' : 'Create New Template'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            {/* Panel body */}
            <div className="px-6 py-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => updateForm('title', e.target.value)}
                  placeholder="e.g. Project Tracker Pro"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  readOnly
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500"
                />
                <p className="text-xs text-gray-400 mt-1">Auto-generated from title</p>
              </div>

              {/* Platform + Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                  <select
                    value={form.platform}
                    onChange={(e) => updateForm('platform', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => updateForm('category', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Pricing */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pricing</label>
                <div className="flex items-center gap-4 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pricing"
                      value="free"
                      checked={form.pricing === 'free'}
                      onChange={() => updateForm('pricing', 'free')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">Free</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pricing"
                      value="premium"
                      checked={form.pricing === 'premium'}
                      onChange={() => updateForm('pricing', 'premium')}
                      className="text-blue-600"
                    />
                    <span className="text-sm">Premium</span>
                  </label>
                </div>
                {form.pricing === 'premium' && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.price}
                      onChange={(e) => updateForm('price', e.target.value)}
                      className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Short Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                <textarea
                  value={form.shortDescription}
                  onChange={(e) => updateForm('shortDescription', e.target.value)}
                  rows={2}
                  placeholder="Brief summary shown in listings..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Long Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Long Description</label>
                <textarea
                  value={form.longDescription}
                  onChange={(e) => updateForm('longDescription', e.target.value)}
                  rows={5}
                  placeholder="Detailed template description..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
                />
                <p className="text-xs text-gray-400 mt-1">Supports markdown</p>
              </div>

              {/* URLs */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template URL</label>
                  <input
                    type="url"
                    value={form.templateUrl}
                    onChange={(e) => updateForm('templateUrl', e.target.value)}
                    placeholder="https://..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preview URL</label>
                  <input
                    type="url"
                    value={form.previewUrl}
                    onChange={(e) => updateForm('previewUrl', e.target.value)}
                    placeholder="https://..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL</label>
                  <input
                    type="url"
                    value={form.thumbnail}
                    onChange={(e) => updateForm('thumbnail', e.target.value)}
                    placeholder="https://..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Screenshots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Screenshots</label>
                {form.screenshots.map((url, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => updateScreenshot(i, e.target.value)}
                      placeholder="Screenshot URL..."
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => removeScreenshot(i)}
                      className="p-1.5 rounded hover:bg-red-50 text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addScreenshot}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus size={14} /> Add Screenshot
                </button>
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
                <input
                  type="url"
                  value={form.videoUrl}
                  onChange={(e) => updateForm('videoUrl', e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => updateForm('tags', e.target.value)}
                  placeholder="project, management, dashboard (comma-separated)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">Comma-separated list of tags</p>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
                {form.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={f}
                      onChange={(e) => updateListItem('features', i, e.target.value)}
                      placeholder="Feature description..."
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {form.features.length > 1 && (
                      <button
                        onClick={() => removeListItem('features', i)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => addListItem('features')}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus size={14} /> Add Feature
                </button>
              </div>

              {/* Use Cases */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Use Cases</label>
                {form.useCases.map((u, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={u}
                      onChange={(e) => updateListItem('useCases', i, e.target.value)}
                      placeholder="Use case..."
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {form.useCases.length > 1 && (
                      <button
                        onClick={() => removeListItem('useCases', i)}
                        className="p-1.5 rounded hover:bg-red-50 text-red-500"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => addListItem('useCases')}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus size={14} /> Add Use Case
                </button>
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select
                  value={form.difficulty}
                  onChange={(e) => updateForm('difficulty', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {DIFFICULTY_OPTIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Featured Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Featured Template</label>
                <button
                  type="button"
                  onClick={() => updateForm('featured', !form.featured)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    form.featured ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      form.featured ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => updateForm('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>

            {/* Panel footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Save size={16} />
                {isEditing ? 'Save Changes' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManager;
